import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { stashPostOnboardingRedirect } from "@/utils/postSignupRedirect";
import { Eye, EyeOff, Mail, Lock, User, MapPin, CheckCircle } from "lucide-react";

function formatError(detail) {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

const perks = [
  "Match with players at your exact skill level",
  "Browse venues with live availability",
  "Travel mode for play in any city",
  "Safe, verified community",
];

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({ name: "", email: "", password: "", city: "Fort Worth, TX" });
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // A visitor who arrived from a search result reaches us as
  // /register?next=/find-partners?sport=tennis. Onboarding is still required for
  // a new account, so the destination is stashed and replayed once onboarding
  // completes; it cannot be navigated to directly.
  const next = searchParams.get("next");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 6) { setError("Password must be at least 6 characters"); return; }
    setLoading(true);
    try {
      await register(form.name, form.email, form.password, form.city);
      // sanitizeRedirect drops anything that is not a same-origin path, so an
      // attacker-supplied `next` cannot turn sign-up into an open redirect onto
      // a lookalike domain right after a genuine registration.
      stashPostOnboardingRedirect(next);
      navigate("/onboarding");
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 pt-20 pb-12">
      <div className="w-full max-w-4xl grid md:grid-cols-2 gap-8 items-center">
        {/* Left: brand + perks */}
        <div className="hidden md:block">
          <div className="flex items-center gap-2 mb-6">
            <img src="/logo.png" alt="ActivitySports" className="h-14 w-auto object-contain" />
          </div>
          <h2 className="text-4xl font-heading font-black text-brand-navy mb-3 leading-tight">
            Find your <span className="text-brand-green">perfect</span> play partner
          </h2>
          <p className="text-gray-500 mb-8 text-sm leading-relaxed">
            Join 1,800+ players who use ActivitySports to find compatible partners for tennis, pickleball, golf, and more across 50+ cities.
          </p>
          <ul className="space-y-3">
            {perks.map((perk) => (
              <li key={perk} className="flex items-center gap-3 text-sm text-gray-600">
                <CheckCircle size={16} className="text-brand-green flex-shrink-0" />
                {perk}
              </li>
            ))}
          </ul>
        </div>

        {/* Right: form */}
        <div>
          <div className="text-center mb-6 md:text-left">
            <h1 className="text-2xl font-heading font-bold text-brand-navy mb-1">Create your free account</h1>
            <p className="text-gray-500 text-sm">No credit card required</p>
          </div>

          <form
            onSubmit={handleSubmit}
            className="bg-white border border-gray-200 rounded-2xl p-6 space-y-4 shadow-sm"
            data-testid="register-form"
          >
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-600" data-testid="register-error">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="Full-Name" className="block text-sm font-medium text-brand-navy mb-1.5">Full name</label>
              <div className="relative">
                <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600" />
                <input id="Full-Name"
                  type="text" required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Alex Johnson"
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl pl-10 pr-4 py-2.5 text-brand-navy placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-green/40 focus:border-brand-green transition-colors text-sm"
                  data-testid="register-name-input"
                />
              </div>
            </div>

            <div>
              <label htmlFor="Email-Address" className="block text-sm font-medium text-brand-navy mb-1.5">Email address</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600" />
                <input id="Email-Address"
                  type="email" required autoComplete="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="you@example.com"
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl pl-10 pr-4 py-2.5 text-brand-navy placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-green/40 focus:border-brand-green transition-colors text-sm"
                  data-testid="register-email-input"
                />
              </div>
            </div>

            <div>
              <label htmlFor="Password" className="block text-sm font-medium text-brand-navy mb-1.5">Password</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600" />
                <input id="Password"
                  type={showPass ? "text" : "password"} required autoComplete="new-password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="At least 6 characters"
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl pl-10 pr-10 py-2.5 text-brand-navy placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-green/40 focus:border-brand-green transition-colors text-sm"
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  aria-label={showPass ? "Hide password" : "Show password"}
                  aria-pressed={showPass}
                  className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center w-9 h-9 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                >
                  {showPass ? <EyeOff size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="Your-City" className="block text-sm font-medium text-brand-navy mb-1.5">Your city</label>
              <div className="relative">
                <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600" />
                <input id="Your-City"
                  type="text"
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  placeholder="Fort Worth, TX"
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl pl-10 pr-4 py-2.5 text-brand-navy placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-green/40 focus:border-brand-green transition-colors text-sm"
                  data-testid="register-city-input"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 font-bold bg-brand-orange text-white rounded-xl hover:bg-brand-orange-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              data-testid="register-submit-button"
            >
              {loading ? "Creating account..." : "Create Free Account"}
            </button>

            <p className="text-center text-sm text-gray-500">
              Already have an account?{" "}
              <Link to="/login" className="text-brand-green hover:underline font-medium">Sign in</Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
